import { useEffect, useState } from 'react';
import { ExternalLink, Music, Music2, ShoppingBag, Play, Radio, Waves } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import * as api from '@/lib/api';
import { supabase } from '@/lib/supabase';
import type { DatabasePlaylistLink } from '@/lib/supabase';

interface PlaylistLinksProps {
  playlistType: 'season' | 'round' | 'league' | 'curated' | 'personal';
  playlistId: string;
  className?: string;
  // For seasons, playlistId might be a season name, so we can optionally provide the actual season ID
  seasonId?: string;
}

const platformConfig = {
  spotify: {
    name: 'Spotify',
    icon: Music2, // Using Music2 as Spotify icon alternative
    color: 'bg-green-500 hover:bg-green-600',
    textColor: 'text-white',
  },
  apple_music: {
    name: 'Apple Music',
    icon: Music, // Using Music as Apple Music icon alternative
    color: 'bg-pink-500 hover:bg-pink-600',
    textColor: 'text-white',
  },
  amazon_music: {
    name: 'Amazon Music',
    icon: ShoppingBag,
    color: 'bg-orange-500 hover:bg-orange-600',
    textColor: 'text-white',
  },
  youtube: {
    name: 'YouTube',
    icon: Play, // Using Play as YouTube icon alternative
    color: 'bg-red-500 hover:bg-red-600',
    textColor: 'text-white',
  },
  tidal: {
    name: 'Tidal',
    icon: Waves,
    color: 'bg-blue-500 hover:bg-blue-600',
    textColor: 'text-white',
  },
  deezer: {
    name: 'Deezer',
    icon: Radio,
    color: 'bg-purple-500 hover:bg-purple-600',
    textColor: 'text-white',
  },
  soundiiz: {
    name: 'Soundiiz',
    icon: Music,
    color: 'bg-indigo-500 hover:bg-indigo-600',
    textColor: 'text-white',
  },
} as const;

export function PlaylistLinks({ playlistType, playlistId, className, seasonId }: PlaylistLinksProps) {
  const [links, setLinks] = useState<DatabasePlaylistLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        setIsLoading(true);
        // For seasons, use seasonId if provided, otherwise try to find season by name
        let actualPlaylistId = playlistId;
        
        if (playlistType === 'season' && !seasonId) {
          // Try to find season ID by name - need to query seasons table directly
          try {
            const { data: seasons } = await supabase
              .from('seasons')
              .select('id, name')
              .eq('name', playlistId);
            
            if (seasons && seasons.length > 0) {
              actualPlaylistId = seasons[0].id;
            }
          } catch (error) {
            console.warn('Could not resolve season ID, using name:', error);
          }
        } else if (playlistType === 'season' && seasonId) {
          actualPlaylistId = seasonId;
        }
        
        const data = await api.playlistLinkAPI.getPlaylistLinks(playlistType, actualPlaylistId);
        setLinks(data);
      } catch (error) {
        console.error('Failed to fetch playlist links:', error);
        setLinks([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLinks();
  }, [playlistType, playlistId, seasonId]);

  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-2 text-sm text-muted-foreground', className)}>
        <Music className="h-4 w-4 animate-spin" />
        <span>Loading playlist links...</span>
      </div>
    );
  }

  if (links.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <span className="text-sm font-medium text-muted-foreground">Available on:</span>
      {links.map((link) => {
        const config = platformConfig[link.platform];
        const Icon = config.icon;

        return (
          <Button
            key={link.id}
            variant="outline"
            size="sm"
            className={cn(
              'gap-2 border-2 transition-all',
              config.color,
              config.textColor,
              'hover:scale-105'
            )}
            onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{config.name}</span>
            <ExternalLink className="h-3 w-3" />
          </Button>
        );
      })}
    </div>
  );
}

